import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { BarChart2, FileText, Truck, RefreshCw, DollarSign, Clock, Timer } from 'lucide-react';
import EvidenceDashboard from '../components/dashboards/Cliente-evidence';
import DeliveryComplianceDashboard from '../components/dashboards/Cliente-deliverycompliance';
import PickupComplianceDashboard from '../components/dashboards/Cliente-pickupcompliance';
import logoamerisa from '../assets/logoamerisa.png';

// Componente contenedor para eventos de navegación
const NavigationWrapper = ({ children }) => {
  const ref = useRef(null);
  
  useEffect(() => {
    const clickHandler = (e) => {
      const sidebarElement = document.querySelector('.sidebar-navigation');
      if (sidebarElement && sidebarElement.contains(e.target)) {
        // Si el clic fue en el sidebar, capturamos el evento para manejo especial
        e.stopPropagation();
      }
    };

    // Capturar eventos de clic que podrían interferir con la navegación
    document.addEventListener('click', clickHandler, true);
    
    return () => {
      document.removeEventListener('click', clickHandler, true);
    };
  }, []);

  return (
    <div ref={ref} className="navigation-wrapper" style={{ isolation: 'isolate' }}>
      {children}
    </div>
  );
};

const Utilidades = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeItem, setActiveItem] = useState('Utilidades');
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDashboard, setSelectedDashboard] = useState('evidence');
  const [isLoading, setIsLoading] = useState(false);
  const initialRender = useRef(true);
  
  // Effect para configurar el entorno y limpiar cuando se desmonte
  useEffect(() => {
    // Marcar los elementos del sidebar con una clase especial para identificación
    const sidebarItems = document.querySelectorAll('.sidebar-item');
    sidebarItems.forEach(item => {
      item.classList.add('sidebar-navigation');
    });

    // Limpiar cuando el componente se desmonta
    return () => {
      document.body.style.overflow = '';
      initialRender.current = true;
    };
  }, []);
  
  // Effect para manejar la navegación cuando cambia la ubicación
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    
    // Si la ubicación cambió y no estamos en la página de utilidades, significa que navegamos fuera
    if (location.pathname !== '/utilities') {
      // Habilitar la navegación normal después de salir de la página de utilidades
      document.body.style.overflow = '';
    }
  }, [location.pathname]);

  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.clear();
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  const dashboards = [
    { id: 'evidence', name: 'Evidencia Cargada (POD)', icon: <FileText size={20} /> },
    //{ id: 'deliveryCompliance', name: 'Cumplimiento de Entregas (OTD)', icon: <Timer size={20} /> },
    //{ id: 'pickupCompliance', name: 'Cumplimiento de Cargas (OTL)', icon: <Clock size={20} /> }
  ];

  const renderDashboard = () => {
    switch (selectedDashboard) {
      case 'evidence':
        return (
          <div className="dashboard-container" style={{ pointerEvents: 'auto' }}>
            <EvidenceDashboard />
          </div>
        );
      case 'trips':
        return <div className="text-center py-12 text-gray-500">Dashboard de Viajes (próximamente)</div>;
      case 'operations':
        return <div className="text-center py-12 text-gray-500">Dashboard de Operaciones (próximamente)</div>;
      case 'costs':
        return <div className="text-center py-12 text-gray-500">Dashboard de Costos (próximamente)</div>;
      case 'timing':
        return <div className="text-center py-12 text-gray-500">Dashboard de Tiempos (próximamente)</div>;
      case 'deliveryCompliance':
        return (
          <div className="dashboard-container" style={{ pointerEvents: 'auto' }}>
            <DeliveryComplianceDashboard />
          </div>
        );
      case 'pickupCompliance':
        return (
          <div className="dashboard-container" style={{ pointerEvents: 'auto' }}>
            <PickupComplianceDashboard />
          </div>
        );
      default:
        return <div>Seleccione un dashboard</div>;
    }
  };

  return (
    <NavigationWrapper>
      <div className="flex">
        <Sidebar
          isOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          onLogout={handleLogout}
        />
        <div className={`transition-all flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} bg-gray-100 min-h-screen`}>
          <Topbar showSearch={false} />
          
          <div className="p-6">
            <div className="bg-white rounded-lg shadow-md mb-8">
              <div className="p-5 border-b border-gray-200">
                <h1 className="text-2xl font-semibold text-gray-800 flex items-center">
                  <BarChart2 className="mr-2 text-blue-600" size={24} /> Dashboard de Utilidades
                </h1>
                <p className="text-gray-600 mt-1">
                  Análisis de datos para mejor toma de decisiones
                </p>
              </div>

              {/* Dashboard Navigation */}
              <div className="flex overflow-x-auto p-2 bg-gray-50 border-b">
                {dashboards.map((dashboard) => (
                  <button
                    key={dashboard.id}
                    onClick={() => setSelectedDashboard(dashboard.id)}
                    className={`flex items-center px-4 py-3 mx-1 rounded-md transition-all ${
                      selectedDashboard === dashboard.id
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="mr-2">{dashboard.icon}</span>
                    <span className="font-medium">{dashboard.name}</span>
                  </button>
                ))}
              </div>

              {/* Dashboard Content */}
              <div className="p-6">
                {isLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  renderDashboard()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </NavigationWrapper>
  );
};

export default Utilidades;
